import type {
  AdInformation,
  AstAdError,
  AstAdObj,
  ConfigAndLoadAdPlacement,
} from "@omni/ads";

import { verticalVideoYAxisFixedScaling } from "./constants";
import { getApnTag, isBannerAd, isNativeAd } from "./helpers";
import { loadAds } from "./helpers/bidding";
import { getKeywords } from "./helpers/getKeywords";

// Configure and load ad for specified adRule (placement)
const configureAndLoadAdPlacement = async ({
  id,
  adRule,
  user,
  article,
  topicName,
  consentToAll,
  geoTracking,
  onAvailable,
  onFailure,
  notAvailable,
}: ConfigAndLoadAdPlacement): Promise<AdInformation> => {
  const [apntag, keywords] = await Promise.all([
    getApnTag(),
    getKeywords({
      geoLocationTracking: geoTracking,
      user,
      topicName,
      article,
      consentToAll,
    }),
  ]);

  return new Promise((resolve, reject) => {
    if (!adRule.appnexus_data) {
      return;
    }

    try {
      apntag.anq.push(async () => {
        apntag.onEvent("adNoBid", id, (e: AstAdObj) => {
          if (notAvailable) {
            notAvailable();
          }
          reject(e);
        });
        apntag.onEvent("adRequestFailure", id, (e: AstAdError) => {
          if (onFailure) {
            onFailure();
          }
          reject(e);
        });
        apntag.onEvent("adError", id, (e: AstAdError, obj: AstAdObj) => {
          if (onFailure) {
            onFailure();
          }
          reject(e);
        });
        apntag.onEvent("adBadRequest", id, (e: AstAdError) => {
          if (onFailure) {
            onFailure();
          }
          reject(e);
        });
        apntag.onEvent("adAvailable", id, (e: AstAdObj) => {
          if (
            e.targetId?.includes("fullscreen") ||
            e.targetId?.includes("takeover")
          ) {
            onAvailable(e);
          }
        });

        apntag.onEvent("adLoaded", id, (e: AstAdObj) => {
          if (isNativeAd(e)) {
            apntag.fireImpressionTrackers(e);

            resolve({
              stylesFromXandr: {},
              nativeAd: e.native,
              creativeId: e.creativeId?.toString(),
            });
          }

          if (isBannerAd(e)) {
            const subContainer = document.querySelector(
              `#div_utif_${id}`,
            ) as HTMLDivElement;
            const ad = e;

            const initalAdWidth = ad?.width <= 5 ? "100%" : `${ad?.width}`;

            const initalAdHeight = ad?.height <= 5 ? "100%" : `${ad?.height}`;

            // FSS === fullscreen scroll
            const isRegularFSS =
              (ad?.height === 3 && ad?.width === 3) ||
              (ad?.height === 1080 && ad?.width === 1920);

            const isVideoFSS = ad?.width === 1080 && ad?.height === 1920;

            const isFSS = isVideoFSS || isRegularFSS;

            const FSSWidth = "100%";
            const FSSHeigh = isVideoFSS
              ? `${verticalVideoYAxisFixedScaling.height}px`
              : "100vh";

            if (subContainer) {
              subContainer.style.width = isFSS ? FSSWidth : initalAdWidth;
              subContainer.style.height = isFSS ? FSSHeigh : "100%";
              const iframe = subContainer.querySelector("iframe");

              if (iframe) {
                iframe.style.width = isFSS ? FSSWidth : initalAdWidth;
                iframe.style.height = isFSS ? FSSHeigh : initalAdHeight;
              }
            }
            const adWidth = isFSS ? FSSWidth : initalAdWidth;
            const adHeight = isFSS ? FSSHeigh : initalAdHeight;

            resolve({
              stylesFromXandr: {
                width: adWidth,
                height: adHeight,
              },
              creativeId: e.creativeId?.toString(),
            });
          }

          reject(new Error(`Unknown ad type, ${e}`));
        });

        loadAds([id], keywords).then(() => {
          apntag.showTag(id);
        });
      });
    } catch (e) {
      console.error("appnexus errored during apntag work", e);
      throw e;
    }
  });
};

export { configureAndLoadAdPlacement };
